import React, { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import moment from 'moment'
import API from '../../../services/API'

// Authorised displays: every unlocked TV is a device with its own revocable
// token. Revoking relocks the display within its 60-second polling interval.
const DevicesPanel = ({ boards, setToast }) => {
  const [devices, setDevices] = useState(null)

  const load = useCallback(async () => {
    const res = await API.get('/wallboard/devices', { skipGlobalErrorHandler: true })
    setDevices(res.status === 200 ? res.data : [])
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const updateDevice = async (id, body) => {
    const res = await API.put(`/wallboard/devices/${id}`, body)
    if (res.status === 200) load()
    else setToast({ severity: 'error', message: 'Could not update the device' })
  }

  const revoke = async (device) => {
    if (
      !window.confirm(
        `Revoke "${device.name}"? The display locks within a minute and will need a site admin to set it up again.`,
      )
    )
      return
    const res = await API.delete(`/wallboard/devices/${device._id}`)
    if (res.status === 200) {
      setToast({ severity: 'success', message: `"${device.name}" revoked` })
      load()
    } else {
      setToast({ severity: 'error', message: 'Could not revoke the device' })
    }
  }

  if (!devices) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Stack gap={2}>
      <Alert severity="info">
        Displays are authorised from the login screen — open{' '}
        <strong>Set up a wallboard display</strong> on the TV and enter site
        admin credentials. The admin is never signed in on the device, and each
        display can be revoked here at any time.
      </Alert>

      {devices.length === 0 ? (
        <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          No displays have been authorised yet.
        </Typography>
      ) : (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Display</TableCell>
                <TableCell>Wallboard</TableCell>
                <TableCell>Last seen</TableCell>
                <TableCell>Authorised</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {devices.map((device) => (
                <TableRow key={device._id}>
                  <TableCell sx={{ minWidth: 180 }}>
                    <TextField
                      size="small"
                      variant="standard"
                      defaultValue={device.name}
                      inputProps={{ maxLength: 60 }}
                      onBlur={(event) => {
                        const name = event.target.value.trim()
                        if (name && name !== device.name)
                          updateDevice(device._id, { name })
                      }}
                    />
                    {device.lastSeenUserAgent && (
                      <Typography variant="caption" color="text.secondary" display="block" noWrap>
                        {device.lastSeenUserAgent.slice(0, 60)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Select
                      size="small"
                      value={device.wallboard?._id ?? ''}
                      displayEmpty
                      onChange={(event) =>
                        updateDevice(device._id, {
                          wallboard: event.target.value || null,
                        })
                      }
                    >
                      <MenuItem value="">Default wallboard</MenuItem>
                      {boards.map((board) => (
                        <MenuItem key={board._id} value={board._id}>
                          {board.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                  <TableCell>
                    {device.lastSeenAt
                      ? moment(device.lastSeenAt).fromNow()
                      : 'Never'}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {moment(device.createdAt).format('D MMM YYYY')}
                    </Typography>
                    {device.createdBy && (
                      <Typography variant="caption" color="text.secondary">
                        by {device.createdBy.firstName} {device.createdBy.lastName}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      color="error"
                      onClick={() => revoke(device)}
                      sx={{ textTransform: 'none' }}
                    >
                      Revoke
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Stack>
  )
}

export default DevicesPanel
